/**
 * TermsOfServiceContent - Termos de Uso do Servico
 * Termos e condicoes de uso da plataforma
 */

import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
} from '@mui/icons-material';

interface TermsOfServiceContentProps {
  companyName?: string;
  lastUpdated?: string;
}

const TermsOfServiceContent: React.FC<TermsOfServiceContentProps> = ({
  companyName = 'Clinafy',
  lastUpdated = '11 de dezembro de 2025',
}) => {
  return (
    <Box sx={{ color: 'text.primary' }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
        Termos de Uso
      </Typography>

      <Typography variant="body2" color="text.secondary" gutterBottom>
        Ultima atualizacao: {lastUpdated}
      </Typography>

      <Typography variant="body1" paragraph sx={{ mt: 2 }}>
        Bem-vindo ao {companyName}! Estes Termos de Uso regulam o acesso e utilizacao da nossa
        plataforma de gestao de consultorios e clinicas. Ao criar uma conta, voce concorda com
        estes termos.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Section 1 - Acceptance */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            1. Aceitacao dos Termos
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Ao acessar ou utilizar o {companyName}, voce declara ter lido, compreendido e aceito
          integralmente estes Termos de Uso e nossa Politica de Privacidade. Se voce nao concordar
          com algum termo, nao utilize a plataforma.
        </Typography>
        <Typography variant="body2" paragraph>
          Para utilizar nossos servicos, voce deve:
        </Typography>
        <List dense>
          {[
            'Ter pelo menos 18 anos de idade',
            'Possuir capacidade legal para celebrar contratos',
            'Fornecer informacoes verdadeiras e completas',
            'Ser profissional habilitado (quando aplicavel)',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="success" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Section 2 - Services */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SupportIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            2. Descricao dos Servicos
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          O {companyName} e uma plataforma SaaS (Software as a Service) que oferece:
        </Typography>
        <List dense>
          {[
            'Gestao de agenda e agendamento de consultas',
            'Prontuario eletronico e registros clinicos',
            'Controle financeiro e emissao de recibos',
            'Teleconsulta integrada',
            'Gestao de equipe e multiplos usuarios',
            'Relatorios e analises',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CheckIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Section 3 - Account */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            3. Conta e Seguranca
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Ao criar uma conta, voce e responsavel por:
        </Typography>
        <List dense>
          {[
            'Manter a confidencialidade da sua senha',
            'Todas as atividades realizadas em sua conta',
            'Notificar imediatamente qualquer uso nao autorizado',
            'Garantir a veracidade das informacoes cadastradas',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><WarningIcon fontSize="small" color="warning" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Section 4 - Prohibited Uses */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CancelIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            4. Usos Proibidos
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          E expressamente proibido:
        </Typography>
        <List dense>
          {[
            'Usar a plataforma para fins ilegais ou nao autorizados',
            'Violar leis aplicaveis, incluindo LGPD e sigilo profissional',
            'Compartilhar credenciais de acesso com terceiros',
            'Tentar acessar areas restritas ou sistemas nao autorizados',
            'Transmitir virus, malware ou codigos maliciosos',
            'Realizar engenharia reversa do software',
            'Coletar dados de outros usuarios sem autorizacao',
            'Usar bots ou sistemas automatizados nao autorizados',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Section 5 - Payment */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            5. Pagamento e Assinatura
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          <strong>5.1 Planos e Precos:</strong> Os precos dos planos estao disponiveis em nossa
          pagina de precos e podem ser alterados mediante aviso previo de 30 dias.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>5.2 Cobranca:</strong> As assinaturas sao cobradas mensalmente de forma recorrente.
          O ciclo de cobranca inicia na data da contratacao.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>5.3 Cancelamento:</strong> Voce pode cancelar sua assinatura a qualquer momento.
          O acesso permanece ativo ate o final do periodo pago. Nao ha reembolso proporcional.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>5.4 Periodo de Teste:</strong> Planos com periodo de teste gratuito nao requerem
          pagamento ate o termino do periodo. O plano sera convertido automaticamente para pago
          apos o teste, salvo cancelamento.
        </Typography>
      </Box>

      {/* Section 6 - Data */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SecurityIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            6. Propriedade dos Dados
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          <strong>Seus dados sao seus.</strong> Voce mantem total propriedade sobre todos os dados
          inseridos na plataforma, incluindo registros de pacientes, prontuarios e documentos.
        </Typography>
        <Typography variant="body2" paragraph>
          O {companyName} atua apenas como operador dos dados, processando-os conforme suas
          instrucoes e nossa Politica de Privacidade.
        </Typography>
        <Typography variant="body2" paragraph>
          Em caso de encerramento da conta, voce pode exportar seus dados antes da exclusao
          definitiva. Apos 30 dias do cancelamento, os dados serao permanentemente excluidos.
        </Typography>
      </Box>

      {/* Section 7 - Liability */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            7. Limitacao de Responsabilidade
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          O {companyName} e fornecido "como esta". Nao garantimos que o servico sera ininterrupto,
          livre de erros ou completamente seguro.
        </Typography>
        <Typography variant="body2" paragraph>
          Em nenhuma circunstancia seremos responsaveis por:
        </Typography>
        <List dense>
          {[
            'Danos indiretos, incidentais ou consequenciais',
            'Perda de lucros, dados ou oportunidades de negocio',
            'Acoes de terceiros, incluindo hackers',
            'Decisoes clinicas tomadas com base nos registros',
            'Interrupcoes causadas por forca maior',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><WarningIcon fontSize="small" color="action" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
        <Typography variant="body2" paragraph sx={{ mt: 2 }}>
          Nossa responsabilidade maxima e limitada ao valor pago nos ultimos 12 meses de assinatura.
        </Typography>
      </Box>

      {/* Section 8 - Intellectual Property */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            8. Propriedade Intelectual
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          A plataforma {companyName}, incluindo software, design, marca, logos e conteudo, e
          propriedade exclusiva da empresa e protegida por leis de propriedade intelectual.
        </Typography>
        <Typography variant="body2" paragraph>
          Voce recebe uma licenca limitada, nao-exclusiva e revogavel para usar a plataforma
          conforme estes termos. Nenhum direito de propriedade e transferido.
        </Typography>
      </Box>

      {/* Section 9 - Termination */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CancelIcon color="error" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            9. Encerramento
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Podemos suspender ou encerrar sua conta se:
        </Typography>
        <List dense>
          {[
            'Voce violar estes Termos de Uso',
            'Houver suspeita de atividade fraudulenta',
            'O pagamento nao for realizado apos tentativas de cobranca',
            'For exigido por lei ou ordem judicial',
          ].map((item, i) => (
            <ListItem key={i}>
              <ListItemIcon><WarningIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Section 10 - Changes */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            10. Alteracoes nos Termos
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Podemos atualizar estes Termos periodicamente. Alteracoes significativas serao
          comunicadas com 30 dias de antecedencia. O uso continuado apos as alteracoes
          constitui aceitacao dos novos termos.
        </Typography>
      </Box>

      {/* Section 11 - Jurisdiction */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <GavelIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            11. Foro e Legislacao
          </Typography>
        </Box>
        <Typography variant="body2" paragraph>
          Estes Termos sao regidos pelas leis da Republica Federativa do Brasil. Fica eleito
          o foro da comarca de Sao Paulo/SP para dirimir quaisquer controversias, com renuncia
          a qualquer outro, por mais privilegiado que seja.
        </Typography>
      </Box>

      {/* Section 12 - Contact */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SupportIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            12. Contato
          </Typography>
        </Box>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2"><strong>Suporte:</strong> suporte@clinafy.com</Typography>
          <Typography variant="body2"><strong>Juridico:</strong> juridico@clinafy.com</Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Footer */}
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        Ao criar uma conta no {companyName}, voce declara ter lido, compreendido e aceito
        integralmente estes Termos de Uso.
      </Typography>
    </Box>
  );
};

export default TermsOfServiceContent;
